import { useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { api, errorText } from "../../lib/api";
import { useLang } from "../../i18n/LangContext";

const previews = new Map();
const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const PhotoUpload = ({ photos, onChange }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!OK_TYPES.includes(file.type)) return setError(t("v.photoType"));
    if (file.size > 8 * 1024 * 1024) return setError(t("v.photoSize"));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/uploads", fd);
      previews.set(data.id, URL.createObjectURL(file));
      onChange([...photos, { id: data.id, name: file.name }]);
    } catch (err) {
      setError(errorText(err, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-200">{t("wizard.photo")}</p>
      <div className="flex flex-wrap gap-3">
        {photos.map((p) => (
          <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-[#2E2E3A] bg-[#0E0E12]" data-testid="photo-thumb">
            {previews.get(p.id) ? <img src={previews.get(p.id)} alt={p.name} className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center p-1 text-[10px] text-gray-400 break-all">{p.name}</span>}
            <button type="button" aria-label={t("wizard.removePhoto")} onClick={() => onChange(photos.filter((x) => x.id !== p.id))} className="absolute right-1 top-1 rounded-full bg-black/80 p-1 text-white hover:bg-[#DC2626]" data-testid="photo-remove-btn">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {photos.length < 3 && (
          <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#3F3F4E] text-xs text-gray-400 hover:border-[#FACC15] hover:text-[#FACC15] transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-[#FACC15]">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
            {busy ? t("wizard.uploading") : t("wizard.addPhoto")}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={onFile} disabled={busy} data-testid="photo-upload-input" />
          </label>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-500">{t("wizard.photoHint")}</p>
      {error && <p role="alert" className="mt-2 text-sm text-red-400" data-testid="photo-upload-error">{error}</p>}
    </div>
  );
};
